import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Table, Card, Select, Typography, Spin, Alert, Space, Button, Radio, Row, Col } from 'antd';
import { MinusSquareOutlined, PlusSquareOutlined, ReloadOutlined, LeftOutlined, DownOutlined, UpOutlined } from '@ant-design/icons';
import { getFromCacheE, saveExpiryToCache } from '../../utils/cacheUtils';
import { useAILimit } from '../user/AIButtonLimitChecker';
import AIUpgradePrompt from '../../shared/components/AIUpgradePrompt';
import { API_CONFIG, apiClient } from '../../api/config';
import activityTracker from '../user/ActivityTracker';
import { logger } from '../../utils/logger';
import { Bar, Line } from 'react-chartjs-2';
import { useCompany } from '../../shared/context/CompanyContext';
import { useLocation } from 'react-router-dom';
import usePageTracking from '../../utils/usePageTracking';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title as ChartTitle,
  Tooltip,
  Legend,
} from 'chart.js';

// 註冊 Chart.js 組件
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ChartTitle,
  Tooltip,
  Legend
);

const { Option } = Select;
const { Title } = Typography;

const Income = ({ initialSymbol }) => {
  // GA4 页面追踪
  usePageTracking('損益表');
  
  const { companyInfo, updateCompanyInfo } = useCompany();
  const location = useLocation();
  const [incomeData, setIncomeData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [period, setPeriod] = useState('quarter');
  const [expandedKeys, setExpandedKeys] = useState(['revenue', 'netIncome']);
  const [analysisResult, setAnalysisResult] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isAnalysisExpanded, setIsAnalysisExpanded] = useState(true);
  const ws = useRef(null);
  const analysisBufferRef = useRef('');
  const { checkAILimit, showUpgradePrompt, setShowUpgradePrompt, limitInfo } = useAILimit();
  const [barChartType, setBarChartType] = useState('revenue');

  const CACHE_KEY_PREFIX = 'income_data_';

  // 處理 URL 參數中的 symbol
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const urlSymbol = searchParams.get('symbol');
    
    // 優先使用 URL 參數，其次是 initialSymbol，最後是 companyInfo
    const symbolToUse = urlSymbol || initialSymbol || companyInfo?.symbol;
    
    if (symbolToUse && symbolToUse !== companyInfo?.symbol) {
      logger.log('Income: 從 URL 參數或 initialSymbol 獲取到新的 symbol:', symbolToUse);
      
      // 更新 CompanyContext
      updateCompanyInfo({
        symbol: symbolToUse,
        company_name: '',
        sector: '',
        industry: '',
        image: ''
      });
      
      logger.log('Income: 已更新 symbol 到 CompanyContext');
    }
  }, [location.search, initialSymbol, companyInfo?.symbol, updateCompanyInfo]);

  // 使用 useCallback 包装 fetchData 函数
  const fetchData = useCallback(async (symbolToUse, periodToUse, forceRefresh = false) => {
    if (!symbolToUse) return;
    
    setLoading(true);
    setError(null);
    
    // 构造缓存键
    const cacheKey = `${CACHE_KEY_PREFIX}${symbolToUse}_${periodToUse}`;
    
    try {
      // 如果不是强制刷新，尝试从缓存获取数据
      if (!forceRefresh) {
        const cachedData = getFromCacheE(cacheKey);
        if (cachedData) {
          logger.log(`使用缓存的收入数据: ${cacheKey}`);
          setIncomeData(cachedData);
          setLoading(false);
          return;
        }
      }
      
      // 從API獲取數據 - 使用apiClient
      logger.log(`從API獲取收入數據: ${symbolToUse}, ${periodToUse}`);
      const endpoint = API_CONFIG.FINANCIAL.INCOME(symbolToUse, periodToUse);
      const response = await apiClient.get(endpoint);
      logger.log('API響應:', response);
      
      setIncomeData(response.data);
      
      // 使用第一筆資料的date來構造新的cacheKey
      if (response.data && response.data.length > 0) {
        const firstDataDate = response.data[0]?.date;
        const newCacheKey = `${CACHE_KEY_PREFIX}${symbolToUse}_${firstDataDate}`;
        console.log('cacheKey (使用第一筆資料的date):', newCacheKey);
        console.log('第一筆資料的date:', firstDataDate);
        saveExpiryToCache(newCacheKey, response.data, 0.5);
      } else {
        // 如果沒有數據，使用原來的cacheKey
        saveExpiryToCache(cacheKey, response.data, 0.5);
      }
      
    } catch (err) {
      logger.error('獲取收入數據失敗:', err);
      if (err.response) {
        logger.error('響應狀態:', err.response.status);
        logger.error('響應數據:', err.response.data);
      }
      setError(`無法載入收入資料: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  // 当公司信息或期间变化时获取数据
  useEffect(() => {
    if (companyInfo.symbol) {
      fetchData(companyInfo.symbol, period);
    }
  }, [companyInfo.symbol, period, fetchData]);

  // Markdown 轉 HTML（參考 list_10q10k.js）
  const convertMarkdownToHtml = (markdownText) => {
    if (!markdownText) return '';
    let html = markdownText;

    // 表格
    const tableRegex = /(\|[^\n]+\|\n\|[\s\-:|]+\|\n(\|[^\n]+\|\n?)+)/g;
    html = html.replace(tableRegex, (match) => {
      const lines = match.trim().split('\n');
      if (lines.length < 3) return match;
      let tableHtml = '<table style="border-collapse: collapse; width: 100%; margin: 10px 0; border: 1px solid #ddd; font-size: 13px;">';
      lines.forEach((line, index) => {
        if (index === 1) return; // 跳過分隔行
        const cells = line.split('|').filter(cell => cell.trim() !== '');
        if (cells.length === 0) return;
        const tag = index === 0 ? 'th' : 'td';
        tableHtml += '<tr>';
        cells.forEach((cell, cellIndex) => {
          const content = cell.trim();
          let style = '';
          if (index === 0) {
            style = 'background-color: #f2f2f2; font-weight: bold; padding: 6px 4px; border: 1px solid #ddd; text-align: center; font-size: 12px;';
          } else {
            style = 'padding: 6px 4px; border: 1px solid #ddd; text-align: left; font-size: 12px;';
            if (cellIndex === 0) {
              style += ' text-align: left;';
            } else {
              style += ' text-align: center;';
            }
          }
          tableHtml += `<${tag} style="${style}">${content}</${tag}>`;
        });
        tableHtml += '</tr>';
      });
      tableHtml += '</table>';
      return tableHtml;
    });

    // 粗體、斜體
    html = html.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    html = html.replace(/__(.*?)__/g, '<strong>$1</strong>');
    html = html.replace(/\*(.*?)\*/g, '<em>$1</em>');
    html = html.replace(/_(.*?)_/g, '<em>$1</em>');

    // 標題
    html = html.replace(/^#### (.*$)/gim, '<h4 style="margin: 12px 0 6px 0; color: #1890ff; font-size: 16px;">$1</h4>');
    html = html.replace(/^### (.*$)/gim, '<h3 style="margin: 16px 0 8px 0; color: #1890ff; font-size: 18px;">$1</h3>');
    html = html.replace(/^## (.*$)/gim, '<h2 style="margin: 20px 0 12px 0; color: #1890ff; font-size: 20px;">$1</h2>');
    html = html.replace(/^# (.*$)/gim, '<h1 style="margin: 24px 0 16px 0; color: #1890ff; font-size: 22px;">$1</h1>');

    // 列表
    html = html.replace(/^\* (.*$)/gim, '<li style="margin: 4px 0;">$1</li>');
    html = html.replace(/^- (.*$)/gim, '<li style="margin: 4px 0;">$1</li>');

    // 段落
    html = html.replace(/\n\n/g, '</p><p style="margin: 8px 0;">');
    html = html.replace(/^(?!<[h|t|u|o|li])(.*$)/gim, '<p style="margin: 8px 0;">$1</p>');
    html = html.replace(/<p><\/p>/g, '');
    html = html.replace(/<p style="margin: 8px 0;"><\/p>/g, '');

    return html;
  };

  const handleRefresh = () => {
    const symbol = companyInfo.symbol;
    if (!symbol) return;
    
          // 先清除緩存
      clearCompanyCache(symbol);
      
      // 然後強制刷新數據
    fetchData(symbol, period, true);
  };

  // 添加一個函數，用於清除特定公司的緩存數據
  const clearCompanyCache = (symbol) => {
    // 清除該公司的所有期間的緩存
    const periods = ['FY', 'quarter'];
    periods.forEach(p => {
      const key = `${CACHE_KEY_PREFIX}${symbol}_${p}`;
      localStorage.removeItem(key);
      logger.log(`已清除緩存: ${key}`);
    });
  };

  const handlePeriodChange = (value) => {
    setPeriod(value);
  };

  // 嘗試載入AI分析緩存
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const symbolFromUrl = urlParams.get('symbol') || '';
    const symbol = symbolFromUrl || companyInfo.symbol;
    if (!symbol) return;
    try {
      const cached = getFromCacheE(`ai_income_analysis_${symbol}`);
      if (cached && cached.content) {
        setAnalysisResult(cached.content);
      }
    } catch (e) {}
  }, []);

  const handleExpand = (expanded, record, e) => {
    e.stopPropagation();
    
    if (expanded) {
      setExpandedKeys([...expandedKeys, record.key]);
    } else {
      setExpandedKeys(expandedKeys.filter(k => k !== record.key));
    }
  };

  // 將數據轉換為每個財年或季度作為一列，並添加可展開功能
  const transformData = () => {
    if (!incomeData || incomeData.length === 0) return [];

    // 獲取所有列名（除了不需要顯示的列）
    const excludeFields = ['symbol', 'fiscalYear', 'period'];
    const allFields = Object.keys(incomeData[0]).filter(field => !excludeFields.includes(field));

    // 定義欄位的自定義順序和分組關係
    const customOrder = [
      'date',

      // 營收分組
      'revenue',
      'growthRevenuePercent',
      'costOfRevenue',

      // 毛利分組
      'grossProfit',
      'growthGrossProfitPercent',
      'grossProfitMargin',   // 新增毛利率欄位

      // 營運費用分組
      'operatingExpenses',            // 父項目
      'researchAndDevelopmentExpenses',  // 子項目
      'growthResearchAndDevelopmentExpensesPercent',
      'sellingGeneralAndAdministrativeExpenses',
      'otherExpenses',

      // 營業利潤分組
      'operatingIncome',
      'growthOperatingIncomePercent',
      'operatingProfitMargin',   // 新增營業利潤率欄位

      // 其他收入/支出淨額
      'totalOtherIncomeExpensesNet',    // 新增父項
      // 所得稅
      'incomeTaxExpense',               // 新增父項目

      // 淨利相關分組
      'netIncome',
      'netProfitMargin',     // 新增淨利率欄位
      'eps',
      'growthEPSPercent',
      'epsDiluted',

      // 其他財務指標分組
      'otherFinancialIndicators',     // 新增的父項目
      'weightedAverageShsOut',        // 移動到子項目
      'growthWeightedAveragePercent',
      'depreciationAndAmortization',
      'ebitda'
    ];

    // 定義父子項目關係
    const groupMap = {
      // 新增的父子關係
      'revenue': [
        'growthRevenuePercent',
        'costOfRevenue'
      ],
      'grossProfit': [
        'growthGrossProfitPercent',
        'grossProfitMargin'     // 將毛利率添加為毛利的子項目
      ],
      'operatingIncome': [
        'growthOperatingIncomePercent',
        'operatingProfitMargin'  // 將營業利潤率添加為營業利潤的子項目
      ],
      'totalOtherIncomeExpensesNet': [],  // 新增父項目，暫無子項目
      'incomeTaxExpense': [],             // 新增父項目，暫無子項目

      // 原有的父子關係
      'operatingExpenses': [
        'researchAndDevelopmentExpenses',
        'growthResearchAndDevelopmentExpensesPercent',
        'sellingGeneralAndAdministrativeExpenses',
        'otherExpenses'
      ],
      'netIncome': [
        'netProfitMargin',     // 將淨利率添加為淨利的子項目
        'eps',
        'growthEPSPercent',
        'epsDiluted'
      ],
      'otherFinancialIndicators': [   // 新增父項目關係
        'weightedAverageShsOut',
        'growthWeightedAveragePercent',
        'depreciationAndAmortization',
        'ebitda'
      ]
    };
    // 收集所有子項目
    const allChildren = Object.values(groupMap).flat();

    // 按自定義順序排序欄位
    const sortedFields = [...allFields].sort((a, b) => {
      const indexA = customOrder.indexOf(a);
      const indexB = customOrder.indexOf(b);

      // 如果欄位不在自定義順序中，放到最後
      if (indexA === -1) return 1;
      if (indexB === -1) return -1;

      return indexA - indexB;
    });

    // 創建一個映射，存儲每個欄位的數據行
    const rowsMap = {};
    // 提前計算所有列標識，避免重複計算
    const columnKeys = [];
    incomeData.forEach(data => {
      columnKeys.push({
        data: data,
        key: period === 'quarter' 
          ? `${data.fiscalYear}${data.period}`
          : data.fiscalYear
      });
    });

    // 輔助函數：填充行數據
    const fillRowData = (rowData, field) => {
      columnKeys.forEach(({data, key}) => {
        rowData[key] = field === 'otherFinancialIndicators' ? '' : data[field];
      });
      return rowData;
    };

    // 先創建所有主項目的行
    sortedFields.forEach(field => {
      // 跳過子項目，後面會作為children添加
      if (allChildren.includes(field)) return;

      // 創建行數據對象
      const rowData = { 
        field: field,
        key: field
      };

      // 填充行數據
      fillRowData(rowData, field);
      rowsMap[field] = rowData;
      // 如果是父項目，預先設置一個空的children數組
      if (groupMap[field]) {
        rowData.children = [];
      }
    });

    // 添加虛擬父項目（如果不在原始數據中）
    if (!rowsMap['otherFinancialIndicators']) {
      const rowData = { 
        field: 'otherFinancialIndicators',
        key: 'otherFinancialIndicators',
        children: []
      };

      // 填充空值
      fillRowData(rowData, 'otherFinancialIndicators');
      rowsMap['otherFinancialIndicators'] = rowData;
    }

    // 添加子項目
    Object.entries(groupMap).forEach(([parent, children]) => {
      if (!rowsMap[parent]) return;

      children.forEach(childField => {
        // 確保字段存在於數據中
        if (!sortedFields.includes(childField)) return;

        const childRowData = { 
          field: childField,
          key: childField
        };

        // 填充子項目數據
        fillRowData(childRowData, childField);

        rowsMap[parent].children.push(childRowData);
      });
    });

    // 將映射轉換回數組
    return Object.values(rowsMap);
  };

  // 生成表格列配置
  const generateColumns = () => {
    // 确保 incomeData 是数组
    if (!incomeData || !Array.isArray(incomeData) || incomeData.length === 0) {
      logger.warn('Invalid income data format:', incomeData);
      return [];
    }

    // 定義需要顯示為深藍色的欄位
    const blueTextFields = ['grossProfitMargin', 'operatingProfitMargin', 'netProfitMargin'];

    // 第一列顯示字段名
    const columns = [
      {
        title: '財務年(季)度',
        dataIndex: 'field',
        key: 'field',
        width: 120,
        fixed: 'left',
        render: (value, record) => {
          // 美化字段名顯示
          const fieldLabels = {
            'date': '結算日期',
            'revenue': '營收',
            'growthRevenuePercent': '營收YoY',
            'costOfRevenue': '營收成本',
            'grossProfit': '毛利',
            'growthGrossProfitPercent': '毛利YoY',
            'grossProfitMargin': '毛利率',
            'operatingExpenses': '營運費用',
            'researchAndDevelopmentExpenses': '研發費用',
            'growthResearchAndDevelopmentExpensesPercent': '研發費用YoY',
            'sellingGeneralAndAdministrativeExpenses': '銷售及管理費用',
            'otherExpenses': '其他費用',
            'operatingIncome': '營業利潤',
            'growthOperatingIncomePercent': '營業利潤YoY',
            'operatingProfitMargin': '營業利潤率',
            'totalOtherIncomeExpensesNet': '業外收入',
            'incomeTaxExpense': '所得稅費用',
            'netIncome': '淨利',
            'netProfitMargin': '淨利率',
            'eps': 'EPS',
            'growthEPSPercent': 'EPS YoY',
            'epsDiluted': '稀釋後EPS',
            'otherFinancialIndicators': '其他財務指標',
            'weightedAverageShsOut': '加權平均流通股數',
            'growthWeightedAveragePercent': '加權平均流通股數YoY',
            'depreciationAndAmortization': '折舊與攤銷',
            'ebitda': 'EBITDA'
          };

          const displayName = fieldLabels[value] || value;

          // 為利潤率欄位設置深藍色
          const textStyle = blueTextFields.includes(value) ? { color: '#1a2952', fontWeight: 'bold' } : {};

          // 如果有子項目，添加展開/縮放圖標
          if (record.children && record.children.length > 0) {
            return (
              <span className="expandable-row-label">
                <span style={textStyle}>{displayName}</span>
                <span className="expand-icon" onClick={(e) => {
                  // 獲取樹的展開狀態
                  const expanded = expandedKeys.includes(record.key);
                  // 觸發展開/收起
                  handleExpand(!expanded, record, e);
                  e.stopPropagation();
                }}>
                  {expandedKeys.includes(record.key) ? 
                    <MinusSquareOutlined style={{ marginLeft: 8 }} /> : 
                    <PlusSquareOutlined style={{ marginLeft: 8 }} />
                  }
                </span>
              </span>
            );
          }

          return <span style={textStyle}>{displayName}</span>;
        }
      }
    ];

    // 創建一個集合來存儲唯一的列標識
    const columnKeys = new Set();

    // 為每個財年/季度的數據添加列定義
    incomeData.forEach(data => {
      // 根據 period 參數決定如何創建列標識
      const columnKey = period === 'quarter' 
        ? `${data.fiscalYear}${data.period}` // 季度數據
        : data.fiscalYear;                    // 年度數據

      // 避免重複列
      if (!columnKeys.has(columnKey)) {
        columnKeys.add(columnKey);

        columns.push({
          title: columnKey,
          dataIndex: columnKey,
          key: columnKey,
          align: 'right',
          width: 100,
          render: (text, record) => {
            // 對於成長率欄位，根據正負值設置顏色
            if (record.field.includes('growth') && record.field.includes('Percent')) {
              // 檢查是否包含負號（如 "-2.5 %"）
              const isNegative = text && text.includes('-');
              
              return (
                <span style={{ 
                  color: isNegative ? '#ff4d4f' : '#52c41a',  // 負數紅色，正數綠色
                  fontWeight: 'bold'
                }}>
                  {text}
                </span>
              );
            }

            // 對於利潤率欄位，設置深藍色
            if (blueTextFields.includes(record.field)) {
              return (
                <span style={{ 
                  color: '#1a2952',  // 深藍色 
                  fontWeight: 'bold'
                }}>
                  {text}
                </span>
              );
            }
            // 其他欄位維持原樣
            return text;
          }
        });
      }
    });

    // 確保列按時間順序排序（最新的在左側）
    columns.sort((a, b) => {
      if (a.dataIndex === 'field') return -1; // 項目列始終在最左側
      if (b.dataIndex === 'field') return 1;

      // 直接比較字符串表示
      return String(b.dataIndex) > String(a.dataIndex) ? 1 : -1;
    });

    return columns;
  };

  const handleAnalyze = async () => {
    if (!companyInfo.symbol) return;
    const limitCheck = await checkAILimit('AI-income');
    if (!limitCheck.canProceed) {
      setAnalysisResult(limitCheck.reason || 'AI 使用次數已達上限');
      return;
    }
    
    setIsAnalyzing(true);
    setAnalysisResult('');
    analysisBufferRef.current = '';
    
    const wsProtocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
    const wsHost = window.location.hostname === 'localhost'
      ? 'localhost:5080'
      : window.location.host;
    const wsUrl = `${wsProtocol}://${wsHost}/ws-app`;

    if (!ws.current || ws.current.readyState !== WebSocket.OPEN) {
      ws.current = new WebSocket(wsUrl);
      
      ws.current.onopen = () => {
        ws.current.send(JSON.stringify({
          type: 'income-analysis',
          symbol: companyInfo.symbol
        }));
      };
      
      ws.current.onmessage = (event) => {
        const data = JSON.parse(event.data);
        if (data.type === 'income') {
          analysisBufferRef.current += data.content || '';
          const html = convertMarkdownToHtml(analysisBufferRef.current);
          setAnalysisResult(html);
        } else if (data.type === 'complete') {
          try {
            const cacheKey = `ai_income_analysis_${companyInfo.symbol}`;
            const finalHtml = convertMarkdownToHtml(analysisBufferRef.current);
            saveExpiryToCache(cacheKey, {
              symbol: companyInfo.symbol,
              content: finalHtml,
              updatedAt: Date.now()
            }, 90);
          } catch (e) {}
          setIsAnalyzing(false);
        } else if (data.error) {
          setAnalysisResult(data.error);
          setIsAnalyzing(false);
        }
      };
      
      ws.current.onerror = (error) => {
        console.error('WebSocket error:', error);
        setAnalysisResult('分析過程中發生錯誤');
        setIsAnalyzing(false);
      };
    } else {
      ws.current.send(JSON.stringify({
        type: 'income-analysis',
        symbol: companyInfo.symbol
      }));
    }

    // 添加按钮点击追踪（正確的三參數形式）
    activityTracker.trackBtnExecution('AI-income', companyInfo.symbol, window.location.pathname);
  };

  useEffect(() => {
    
    const urlParams = new URLSearchParams(window.location.search);
    const symbol = urlParams.get('symbol') || '';
    if (symbol) {
      activityTracker.trackPageView(symbol);
    }
    // 嘗試載入AI分析緩存
    const analysisKey = symbol ? `ai_income_analysis_${symbol}` : (companyInfo.symbol ? `ai_income_analysis_${companyInfo.symbol}` : null);
    if (analysisKey) {
      try {
        const cached = getFromCacheE(analysisKey);
        if (cached && cached.content) {
          setAnalysisResult(cached.content);
        }
      } catch (e) {}
    }
  }, []);

  // 准备图表数据
  const prepareChartData = () => {
    if (!incomeData || incomeData.length === 0) return null;

    // 按时间倒序排序数据（最新的在前）
    const sortedData = [...incomeData].sort((a, b) => {
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);
      return dateB - dateA;
    });

    // 只取最近20条数据
    const recentData = sortedData.slice(0, 20);

    // 准备标签（日期）
    const labels = recentData.map(item => {
      const date = new Date(item.date);
      return period === 'quarter' 
        ? `${date.getFullYear()}${item.period}`
        : `${date.getFullYear()}`;
    });

    // 处理百分比数据
    const parsePercentage = (value) => {
      if (typeof value === 'string') {
        return parseFloat(value.replace('%', '').trim());
      }
      return value;
    };

    // 处理带单位的数据
    const parseValueWithUnit = (value) => {
      if (value == null) return 0;
      if (typeof value === 'number') return value;
      if (typeof value === 'string') {
        const normalized = value.replace(/,/g, '').trim();
        // 支援 K/M/B 單位，統一換算為 M（百萬）
        const match = normalized.match(/^(-?\d+(\.\d+)?)(\s*[KMB])?$/i);
        if (match) {
          const num = parseFloat(match[1]);
          const unit = (match[3] || '').trim().toUpperCase();
          if (unit === 'K') return num / 1000;   // 千 -> 百萬
          if (unit === 'M') return num;          // 百萬 -> 百萬
          if (unit === 'B') return num * 1000;   // 十億 -> 百萬（保險處理）
          return num; // 無單位，視為已是 M
        }
        // 後備：移除所有字母再嘗試解析
        const fallback = parseFloat(normalized.replace(/[A-Za-z]/g, ''));
        return isNaN(fallback) ? 0 : fallback;
      }
      return 0;
    };

    // 准备 Bar Chart 数据
    const barData = {
      labels,
      datasets: [
        {
          label: barChartType === 'revenue' ? '營收' : 'EPS',
          data: recentData.map(item => 
            barChartType === 'revenue' 
              ? parseValueWithUnit(item.revenue)
              : item.eps
          ),
          backgroundColor: barChartType === 'revenue' 
            ? 'rgba(54, 162, 235, 0.5)'
            : 'rgba(255, 99, 132, 0.5)',
          borderColor: barChartType === 'revenue'
            ? 'rgba(54, 162, 235, 1)'
            : 'rgba(255, 99, 132, 1)',
          borderWidth: 1,
        }
      ],
    };

    // 准备 Line Chart 数据
    const lineData = {
      labels,
      datasets: [
        {
          label: '毛利率',
          data: recentData.map(item => parsePercentage(item.grossProfitMargin)),
          borderColor: 'rgba(75, 192, 192, 1)',
          backgroundColor: 'rgba(75, 192, 192, 0.2)',
          tension: 0.1,
        },
        {
          label: '營業利潤率',
          data: recentData.map(item => parsePercentage(item.operatingProfitMargin)),
          borderColor: 'rgba(153, 102, 255, 1)',
          backgroundColor: 'rgba(153, 102, 255, 0.2)',
          tension: 0.1,
        },
        {
          label: '淨利率',
          data: recentData.map(item => parsePercentage(item.netProfitMargin)),
          borderColor: 'rgba(255, 159, 64, 1)',
          backgroundColor: 'rgba(255, 159, 64, 0.2)',
          tension: 0.1,
        },
      ],
    };

    return { barData, lineData };
  };

  // 图表配置
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            if (context.parsed.y !== null) {
              // 對於營收數據，添加單位
              if (context.dataset.label === '營收') {
                label += new Intl.NumberFormat('zh-TW', {
                  style: 'decimal',
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2
                }).format(context.parsed.y) + ' M';
              }
              // 對於利潤率數據，添加百分比符號
              else if (['毛利率', '營業利潤率', '淨利率'].includes(context.dataset.label)) {
                label += new Intl.NumberFormat('zh-TW', {
                  style: 'decimal',
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2
                }).format(context.parsed.y) + '%';
              }
              // 其他數據（如EPS）
              else {
                label += new Intl.NumberFormat('zh-TW', {
                  style: 'decimal',
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2
                }).format(context.parsed.y);
              }
            }
            return label;
          }
        }
      }
    },
    scales: {
      x: {
        ticks: {
          maxRotation: 45,
          minRotation: 45,
          callback: function(value, index, values) {
            const label = this.getLabelForValue(value);
            return label;
          },
          font: function(context) {
            // 最近一期（第一个标签）使用粗体
            if (context.index === 0) {
              return {
                weight: 'bold',
                size: 14
              };
            }
            return {
              weight: 'normal',
              size: 12
            };
          }
        },
        grid: {
          display: false
        }
      },
      y: {
        beginAtZero: true,
        ticks: {
          callback: function(value) {
            // 對於營收數據，添加單位
            if (this.chart.data.datasets[0].label === '營收') {
              return new Intl.NumberFormat('zh-TW', {
                style: 'decimal',
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              }).format(value) + ' M';
            }
            // 對於利潤率數據，添加百分比符號
            else if (this.chart.data.datasets.some(dataset => 
              ['毛利率', '營業利潤率', '淨利率'].includes(dataset.label)
            )) {
              return new Intl.NumberFormat('zh-TW', {
                style: 'decimal',
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              }).format(value) + '%';
            }
            // 其他數據
            return new Intl.NumberFormat('zh-TW', {
              style: 'decimal',
              minimumFractionDigits: 2,
              maximumFractionDigits: 2
            }).format(value);
          }
        }
      }
    },
    barPercentage: 0.8,
    categoryPercentage: 0.9
  };

  return (
    <div className="container mt-4">
      <Card
        title={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
            <div className="card-title">
             
              <Button 
                type="primary"
                onClick={handleAnalyze}
                loading={isAnalyzing}
                icon={<ReloadOutlined />}
                style={{ backgroundColor: '#1890ff', borderColor: '#1890ff', fontWeight: 'bold' }}
              >
                {isAnalyzing ? '分析中...' : '分析最新季度'}
              </Button>
            </div>
            <div></div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Select 
                defaultValue="quarter"
                style={{ width: 120 }} 
                onChange={handlePeriodChange}
                loading={loading}
              >
                <Option value="FY">年度</Option>
                <Option value="quarter">季度</Option>
              </Select>
              <Button onClick={handleRefresh} style={{ marginLeft: 0 }}>刷新數據</Button>
            </div>
          </div>
        } 
        className="financial-card"
      >
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          {analysisResult && (
            <Card className="mb-4">
              <div 
                style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  cursor: 'pointer'
                }}
                onClick={() => setIsAnalysisExpanded(!isAnalysisExpanded)}
              >
                <Title level={4} style={{ margin: 0 }}>分析結果</Title>
                {isAnalysisExpanded ? <UpOutlined /> : <DownOutlined />}
              </div>
              {isAnalysisExpanded && (
                <div 
                  className="ai-analysis-html"
                  style={{ lineHeight: '1.6', fontSize: '14px', marginTop: '16px' }}
                  dangerouslySetInnerHTML={{ __html: analysisResult }}
                />
              )}
            </Card>
          )}
          {error && <Alert type="error" message={error} />}
          
          {loading ? (
            <div style={{ textAlign: 'center', margin: '50px 0' }}>
              <Spin size="large" />
            </div>
          ) : (
            <>
              {/* 添加图表 */}
              {incomeData && incomeData.length > 0 && (
                <div style={{ marginTop: '10px', marginBottom: '20px' }}>
                  <Row gutter={[16, 16]}>
                    <Col xs={24} lg={12}>
                      <Card 
                        title={
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span>營收與EPS趨勢</span>
                            <Radio.Group 
                              value={barChartType} 
                              onChange={e => setBarChartType(e.target.value)}
                              buttonStyle="solid"
                              size="small"
                            >
                              <Radio.Button value="revenue">營收</Radio.Button>
                              <Radio.Button value="eps">EPS</Radio.Button>
                            </Radio.Group>
                          </div>
                        } 
                        className="mb-4"
                      >
                        <div style={{ height: '300px', overflowX: 'auto' }}>
                          <div style={{ minWidth: '600px', height: '100%' }}>
                            <Bar data={prepareChartData()?.barData} options={chartOptions} />
                          </div>
                        </div>
                        {/* 添加时间提示 */}
                        <div style={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          alignItems: 'center',
                          marginTop: '5px', 
                          padding: '0 15px',
                          fontSize: '14px',
                          color: '#333'
                        }}>
                          <span style={{ fontWeight: 'bold' }}>近期</span>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                            <LeftOutlined style={{ fontSize: '18px', color: '#1890ff', fontWeight: 'bold' }} />
                            <LeftOutlined style={{ fontSize: '18px', color: '#1890ff', fontWeight: 'bold' }} />
                            <LeftOutlined style={{ fontSize: '18px', color: '#1890ff', fontWeight: 'bold' }} />
                          </div>
                          <span style={{ fontWeight: 'bold' }}>遠期</span>
                        </div>
                      </Card>
                    </Col>
                    <Col xs={24} lg={12}>
                      <Card title="利潤率趨勢">
                        <div style={{ height: '300px', overflowX: 'auto' }}>
                          <div style={{ minWidth: '600px', height: '100%' }}>
                            <Line data={prepareChartData()?.lineData} options={chartOptions} />
                          </div>
                        </div>
                        {/* 添加时间提示 */}
                        <div style={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          alignItems: 'center',
                          marginTop: '5px', 
                          padding: '0 15px',
                          fontSize: '14px',
                          color: '#333'
                        }}>
                          <span style={{ fontWeight: 'bold' }}>近期</span>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                            <LeftOutlined style={{ fontSize: '18px', color: '#1890ff', fontWeight: 'bold' }} />
                            <LeftOutlined style={{ fontSize: '18px', color: '#1890ff', fontWeight: 'bold' }} />
                            <LeftOutlined style={{ fontSize: '18px', color: '#1890ff', fontWeight: 'bold' }} />
                          </div>
                          <span style={{ fontWeight: 'bold' }}>遠期</span>
                        </div>
                      </Card>
                    </Col>
                  </Row>
                </div>
              )}

              <Table 
                columns={generateColumns()} 
                dataSource={transformData()} 
                rowKey="field"
                pagination={false}
                scroll={{ x: 'max-content' }} 
                size="middle"
                tableLayout="fixed"
                bordered
                expandedRowKeys={expandedKeys}
                onExpand={(expanded, record) => {
                  handleExpand(expanded, record, { stopPropagation: () => {} });
                }}
                expandable={{
                  expandIcon: () => null, // 隱藏默認的展開圖標
                  expandRowByClick: true
                }}
                onRow={(record) => {
                  return {
                    style: record.field === 'date' ? {
                      backgroundColor: '#FFFBE6',  // 淺黃色背景
                    } : {}
                  };
                }}
              />
            </>
          )}
        </Space>

        <AIUpgradePrompt
          visible={showUpgradePrompt}
          onCancel={() => setShowUpgradePrompt(false)}
          limitInfo={limitInfo}
        />
        <style jsx>{`
          .financial-card {
            margin-bottom: 20px;
          }
          .expandable-row-label {
            display: flex;
            align-items: center;
            justify-content: space-between;
          }
          .expand-icon {
            cursor: pointer;
          }
          .ant-table {
            font-size: 14px;
          }
          .ant-table-thead > tr > th {
            font-size: 14px;
            padding: 8px;
          }
          .ant-table-tbody > tr > td {
            font-size: 14px;
            padding: 8px;
          }
          .ant-card-head-title {
            font-size: 16px;
          }
          .ant-select {
            font-size: 14px;
          }
          .ant-select-selector {
            font-size: 14px !important;
          }
          .ant-btn {
            font-size: 14px;
          }
          .ant-btn[style*="background-color: #ffffff"]:hover {
            background: #f5f5f5 !important;
            border-color: #bfbfbf !important;
            color: #000000 !important;
          }

          @media screen and (max-width: 768px) {
            .ant-table {
              font-size: 11px;
            }
            .ant-table-thead > tr > th {
              font-size: 11px;
              padding: 4px;
            }
            .ant-table-tbody > tr > td {
              font-size: 11px;
              padding: 4px;
            }
            .ant-card-head-title {
              font-size: 14px;
            }
            .ant-select {
              font-size: 11px;
            }
            .ant-select-selector {
              font-size: 11px !important;
            }
            .ant-btn {
              font-size: 11px;
            }
            .ant-table-cell[data-column-key="field"] {
              width: 100px !important;
            }
          }

          @media screen and (max-width: 480px) {
            .ant-table {
              font-size: 12px;
            }
            .ant-table-thead > tr > th {
              font-size: 12px;
              padding: 1px;
            }
            .ant-table-tbody > tr > td {
              font-size: 12px;
              padding: 1px;
            }
            .ant-card-head-title {
              font-size: 14px;
            }
            .ant-select {
              font-size: 12px;
            }
            .ant-select-selector {
              font-size: 12px !important;
            }
            .ant-btn {
              font-size: 12px;
            }
            .ant-table-cell[data-column-key="field"] {
              width: 50px !important;
            }
          }
        `}</style>
      </Card>
    </div>
  );
};

export default Income; 